import { Collapse, Divider, Fieldset, Group, Stack, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { ToggleCollapse } from "@tfg/ui"
import { sortBy } from "lodash"
import { useTranslation } from "react-i18next"

import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"

import { PropertyItemInfo } from "~/components/PropertyItemInfo.tsx"
import { DateCellItemInfo } from "~/components/cells/DateCell.tsx"
import { CompositionItem } from "~/pages/SupplierPortal/TraceabilityRequests/components/MaterialComposition"
import {
  isComponentItem,
  isMaterialItem,
  isProductItem,
} from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import {
  useGetTranslation,
  useCurrentLanguage,
  getTranslation,
} from "~/utils/translations.ts"

interface PropertyField {
  label: string
  value: React.ReactNode
  order: number
  condition?: boolean
}

type Props = {
  request: TraceabilityResponses.TraceabilityRequest
  issuer: {
    companyName: string
    name: string
    email: string
  }
}

const renderPropertyFields = (fields: PropertyField[]) =>
  fields
    .filter((field) => field.condition !== false) // show only fields that pass the condition
    .sort((a, b) => a.order - b.order)
    .map((field, index) => (
      <PropertyItemInfo key={index} label={field.label} value={field.value} />
    ))

export const RequestItemInformation = ({ request, issuer }: Props) => {
  const { t } = useTranslation("traceability")

  const item = request.item
  const label = useGetTranslation(item.itemInformation.type)

  const commonFields: PropertyField[] = [
    {
      label: t("request.labels.requestDate"),
      value: <DateCellItemInfo date={request.createdAt} fz="sm" fw={600} />,
      order: 0,
    },
    {
      label: t("item.reference"),
      value: item.reference,
      order: 2,
    },
    {
      label: t("item.client"),
      value: issuer.companyName,
      order: 6,
      condition: !!issuer.companyName,
    },
  ]

  if (isProductItem(item)) {
    const productFields: PropertyField[] = [
      {
        label: t("item.name"),
        value: item.name,
        order: 1,
        condition: !!item.name,
      },
      {
        label: t("item.brand"),
        value: item.brand,
        order: 5,
        condition: !!item.brand,
      },
      {
        label: t("item.type"),
        value: label,
        order: 3,
        condition: !!item.itemInformation.type,
      },
      {
        label: t("item.collection"),
        value: item.collection,
        order: 4,
        condition: !!item.collection,
      },
      {
        label: t("item.color"),
        value: item.color,
        order: 5,
        condition: !!item.color,
      },
      {
        label: t("item.purchaseOrderRef"),
        value: item.purchaseOrderRef,
        order: 7,
        condition: !!item.purchaseOrderRef,
      },
      {
        label: t("item.marketLaunchDate"),
        value: (
          <DateCellItemInfo date={item.marketLaunchDate} fz="sm" fw={600} />
        ),
        order: 9,
        condition: !!item.marketLaunchDate,
      },
      {
        label: t("item.date"),
        value: (
          <DateCellItemInfo date={item.itemInformation.date} fz="sm" fw={600} />
        ),
        order: 8,
        condition: !!item.itemInformation.date,
      },
    ]

    return (
      <Stack gap="xs">
        {renderPropertyFields([...productFields, ...commonFields])}
        <Divider color="gray.3" />
        <CollapsibleCompositionItem>
          <ProductComponentsComposition
            components={sortBy(item.children, (child) => {
              return child.itemInformation.mainFabric ? 1 : -1
            })}
          />
        </CollapsibleCompositionItem>
      </Stack>
    )
  } else if (isComponentItem(item)) {
    return (
      <Stack gap="xs">
        {renderPropertyFields(commonFields)}
        <Divider color="gray.3" />
        <CollapsibleCompositionItem>
          {item.children.map((child) => (
            <CompositionItem
              key={child.id}
              type={child.itemInformation.type}
              proportion={child.itemInformation.proportion}
            />
          ))}
        </CollapsibleCompositionItem>
      </Stack>
    )
  } else if (isMaterialItem(item)) {
    return <Stack gap="xs">{renderPropertyFields(commonFields)}</Stack>
  }
  return null
}

export const CollapsibleCompositionItem = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [open, { toggle }] = useDisclosure(false)
  const { t } = useTranslation("traceability")
  return (
    <Stack gap="md" w="100%">
      <Group justify="space-between" wrap="nowrap">
        <Text fw={600} fz="sm">
          {t("request.labels.composition")}
        </Text>
        <ToggleCollapse open={open} toggle={toggle} />
      </Group>
      <Collapse in={open}>{children}</Collapse>
    </Stack>
  )
}

export const ProductComponentsComposition = ({
  components,
}: {
  components: TraceabilityResponses.ComponentTraceabilityRequestItem[]
}) => {
  const currentLanguage = useCurrentLanguage()
  return (
    <Stack gap="xs" w="100%">
      {components.map((component, index) => (
        <Fieldset
          key={`${index}-${component.id}`}
          legend={
            <Group justify="center" w="100%">
              {getTranslation(component.itemInformation.type, currentLanguage)}
            </Group>
          }
        >
          {component.children.map((child) => (
            <CompositionItem
              key={child.id}
              type={child.itemInformation.type}
              proportion={child.itemInformation.proportion}
            />
          ))}
        </Fieldset>
      ))}
    </Stack>
  )
}
